import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Transcript, TranscriptCue } from '../player/player.interface';

import { TranscriptHeader } from './transcript-header';
import { Menu } from '../menu/menu';
import { TranscriptItem } from './transcript-item';
import { useOnClickOutside } from '../../hooks/use-on-click-outside';
import { TranscriptMenuButtonName } from './transcript-menu-button';
import { SubtitleOption } from '../user-interfaces';
import { getCurrentActiveCue } from './utils';
import { useTranscriptSearch } from './use-transcript-search';
import { useScrollToTranscriptItem } from './use-scroll-to-transcript-item';

import menuStyles from '../menu/menu.module.css';
import styles from './transcript.module.css';
import { SyncWithVideoButton } from './sync-with-video-button';

const TRANSCRIPT_ITEM_ID_PREFIX = 'transcript-item-';

type TranscriptMenuProps = {
  isOpen?: boolean;
  transcript: Transcript | null | undefined;
  currentTime?: number;
  languages?: SubtitleOption[];
  selectedLanguage?: string;
  onLanguageChange?: (language: string) => void;
  onClose: () => void;
  onTranscriptItemClick?: (cue: TranscriptCue) => void;
  showTranscriptDownload?: boolean;
};

export function TranscriptMenu({
  isOpen,
  transcript,
  currentTime,
  languages,
  selectedLanguage,
  onLanguageChange,
  onClose,
  onTranscriptItemClick,
  showTranscriptDownload,
}: TranscriptMenuProps) {
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(false);

  const { t } = useTranslation();

  const wrapperRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(wrapperRef, onClose, TranscriptMenuButtonName, isOpen);

  const currentActiveCue = useMemo(() => getCurrentActiveCue(transcript?.cues, currentTime), [currentTime, transcript]);

  const { scrollRef, scrollToTranscriptItem } = useScrollToTranscriptItem(TRANSCRIPT_ITEM_ID_PREFIX);

  const { search, onSearch, cuesIndex, searchResultIndex, currentSearchResult, setCurrentSearchResult } =
    useTranscriptSearch(transcript, scrollToTranscriptItem);

  const handleTranscriptItemClick = useCallback(
    (cue: TranscriptCue) => {
      if (onTranscriptItemClick) {
        onTranscriptItemClick(cue);

        // Enable auto-scroll on transcript item click. We need to wait a bit since jumping to timestamp takes longer
        setTimeout(() => {
          setAutoScrollEnabled(true);
        }, 100);
      }
    },
    [onTranscriptItemClick]
  );

  return (
    <div
      ref={wrapperRef}
      className={classNames(menuStyles.wrapper, !isOpen && menuStyles.closed, styles.wrapper)}
      tabIndex={-1}
    >
      <TranscriptHeader
        languages={languages}
        selectedLanguage={selectedLanguage}
        onSelectedLanguageChange={onLanguageChange}
        onClose={onClose}
        onSearch={onSearch}
        searchResultCount={searchResultIndex?.length}
        currentSearchResult={currentSearchResult}
        setCurrentSearchResult={setCurrentSearchResult}
        showTranscriptDownload={showTranscriptDownload}
      />
      <Menu
        ref={scrollRef}
        label={t('ui.menu.item.transcript')}
        onClose={onClose}
        dataTestId="transcript-menu"
        showHeader={false}
      >
        {transcript?.cues?.map((cue, index) => {
          const id = `${TRANSCRIPT_ITEM_ID_PREFIX}${index}`;

          return (
            <TranscriptItem
              id={id}
              key={id}
              data-testid={id}
              cue={cue}
              isActive={!!currentActiveCue && currentActiveCue.startTime === cue.startTime}
              onItemClick={handleTranscriptItemClick}
              search={search}
              activeHighlightedIndex={cuesIndex?.[index]?.indexOf((currentSearchResult ?? 0) - 1)}
            />
          );
        })}

        <SyncWithVideoButton
          itemIdPrefix={TRANSCRIPT_ITEM_ID_PREFIX}
          currentActiveCue={currentActiveCue}
          cues={transcript?.cues}
          scrollToTranscriptItem={scrollToTranscriptItem}
          scrollRef={scrollRef}
          shown={isOpen}
          autoScrollEnabled={autoScrollEnabled}
          setAutoScrollEnabled={setAutoScrollEnabled}
        />
      </Menu>
    </div>
  );
}
